import { useState, useEffect, useRef, useLayoutEffect } from "react";

function useIsMobile(bp = 768) {
  const [mobile, setMobile] = useState(() => window.innerWidth < bp);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return mobile;
}

// Prevents scroll-to-top when a focused element is removed during a re-render
// (e.g. inline confirmation button replacement). Call at the top of any
// component whose state updates swap out focused buttons.
function useScrollPreserve() {
  const _pos = document.querySelector("main")?.scrollTop ?? 0;
  useLayoutEffect(() => {
    const el = document.querySelector("main");
    if (el) el.scrollTop = _pos;
  });
}

// ── useAIContext — universal AI context hook ──────────────────────────────────
// Call once per component: useAIContext("mymodule", () => ({ ...liveData }), [deps])
// The AI Assistant automatically knows about every module that registers this.
// Context is cached in sessionStorage so the AI keeps module knowledge even when
// the user navigates away and the component unmounts.
function useAIContext(key, contextFn, deps = []) {
  useEffect(() => {
    if (!window.__aiCtx) window.__aiCtx = {};
    window.__aiCtx[key] = contextFn;
    // Cache the evaluated context so unmounted modules stay visible to AI
    try {
      const val = contextFn();
      if (val) sessionStorage.setItem(`__aiCtx_${key}`, typeof val === "string" ? val : JSON.stringify(val));
    } catch {}
    return () => {
      if (window.__aiCtx) delete window.__aiCtx[key];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export { useIsMobile, useScrollPreserve, useAIContext };
